import { ApiError } from '@/lib/api'
import type { MessageKey } from '@/lib/i18n/messages/en'
import { en } from '@/lib/i18n/messages/en'
import { type Params, type Translate, t as tGlobal, useT } from '@/lib/i18n/translate'

function isKey(key: string): key is MessageKey {
  return key in en
}

/** Largest unit that fits, rounded up so waiting that long always works. */
export function retryWait(seconds: number, t: Translate): string {
  if (seconds < 60) return t('duration.seconds', { n: Math.max(1, Math.ceil(seconds)) })
  if (seconds < 3600) return t('duration.minutes', { n: Math.ceil(seconds / 60) })
  return t('duration.hours', { n: Math.ceil(seconds / 3600) })
}

/** Params that are codes or raw numbers become localized text before interpolation. */
function localizedParams(err: ApiError, t: Translate): Params {
  if (err.code === 'RATE_LIMITED' || err.code === 'FEEDBACK_DAILY_CAP') {
    return { ...err.params, wait: retryWait(Number(err.params.retry_after_seconds), t) }
  }
  if (err.code === 'DELETE_BLOCKED') {
    const reason = String(err.params.reason ?? '')
    const key = `deleteReason.${reason}`
    return { ...err.params, reason: isKey(key) ? t(key) : reason }
  }
  return err.params
}

/**
 * Any failure → one localized sentence. Anything that is not an `ApiError`
 * (network down, aborted, a bug) reads as INTERNAL.
 */
export function errorMessage(err: unknown, t: Translate = tGlobal): string {
  if (!(err instanceof ApiError)) return t('error.INTERNAL')
  return t(`error.${err.code}`, localizedParams(err, t))
}

export function useErrorMessage(): (err: unknown) => string {
  const t = useT()
  return (err) => errorMessage(err, t)
}
